<template>
  <div>
    <div v-if="!showDetailPage">
      <h2 class="page-title-bar">
        <img src="@/assets/img/camera.png" style="margin-right: 10px; width:28px" />
        {{ $t("Shelf Monitoring") }}
      </h2>
      <div class="mt-5 shelf-monitoring-overview">
        <v-card class="monitor-card">
          <div class="monitor-card-title">
            {{ $t("Shelf Status") }}
          </div>
          <div class="chart-flex-container px-4 pb-3">
            <div class="chart-left-section">
              <div class="chart-container-wrapper">
                <template v-if="hasDonutData">
                  <apexchart type="donut" height="280" :options="statusChartOptions" :series="statusSeries" />
                  <div class="chart-center-text">
                    <div class="center-title">
                      {{ $t("Total") }}
                    </div>
                    <div class="center-count">
                      {{ totalCount }}
                    </div>
                  </div>
                </template>
                <div v-else class="empty-donut-state">
                  <i class="mdi mdi-chart-bar empty-donut-icon"></i>
                  <p class="empty-donut-text">{{ $t('No data available') }}</p>
                </div>
              </div>
            </div>
            <div class="chart-right-section">
              <div v-for="item in statusItems" :key="item.label" class="status-item">
                <div class="item-title pt-1">
                  <span class="status-dot" :style="{ background: item.color }"></span>
                </div>
                <div class="d-flex flex-column" style="gap: 12px;">
                  <div class="item-label">
                    {{ item.label }}
                  </div>
                  <div class="item-value">
                    {{ item.count }}
                  </div>
                  <div class="item-percentage">({{ item.percent }}%)</div>
                </div>
              </div>
            </div>
          </div>
        </v-card>

        <v-card class="monitor-card">
          <div class="monitor-card-head">
            <div class="pa-0 ma-0 monitor-card-title">
              {{ $t("Shelf Trend") }}
            </div>
            <div class="trend-tools">
              <div class="trend-legend">
                <span class="legend-item">
                  <i class="legend-dot oos"></i>
                  {{ $t("OOS") }}
                </span>
                <span class="legend-item">
                  <i class="legend-dot low"></i>
                  {{ $t("Low") }}
                </span>
                <span class="legend-item">
                  <i class="legend-dot misplaced"></i>
                  {{ $t("Misplaced") }}
                </span>
              </div>
              <div class="trend-range-toggle">
                <button v-for="range in ranges" :key="range" class="trend-range-btn"
                  :class="{ active: selectedRange === range }" @click="selectedRange = range">
                  {{ range }}
                </button>
              </div>
            </div>
          </div>
          <div v-if="trendSeries.length">
            <apexchart type="bar" width="100%" height="240" :options="trendOptions" :series="trendSeries" />
          </div>
          <div v-else class="empty-chart-state">
            <i class="mdi mdi-chart-bar empty-icon"></i>
            <p class="empty-text">{{ $t("No data available") }}</p>
          </div>
        </v-card>
      </div>
      <v-row no-gutters class="pa-3">
        <v-col cols="2" class="d-flex align-center justify-center">
          <v-menu v-model="menuOpen" offset-y :close-on-content-click="false" content-class="columnMenu">
            <template v-slot:activator="{ on, attrs }">
              <v-btn text small class="manage-btn btn" v-bind="attrs" v-on="on">
                {{ $t("Manage Columns") }}
                <v-icon right :class="{ rotate: menuOpen }">
                  mdi-chevron-down
                </v-icon>
              </v-btn>
            </template>
            <v-card class="column-card">
              <v-card-title class="column-header">
                {{ $t("Select Columns") }}
              </v-card-title>
              <v-divider />
              <v-list dense class="select-all">
                <v-list-item>
                  <v-checkbox :input-value="isAllSelected" @change="toggleSelectAll" hide-details />
                  <v-list-item-title>
                    {{ $t("Select All") }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
              <v-divider />
              <v-card-text class="column-list">
                <v-list dense>
                  <v-list-item v-for="header in headers
                    .slice(3)" :key="header.value">
                    <v-checkbox v-model="visibleColumns" :value="header.text" hide-details />
                    <v-list-item-title class="column-text">
                      {{ header.text }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-card-text>
              <v-divider />
              <v-card-actions class="column-footer">
                <v-btn color="error" style="background: #c57171;color: #ffffff !important;width:80px" text small
                  @click="menuOpen = false">
                  {{ $t("Cancel") }}
                </v-btn>
                <v-spacer />
                <v-btn color="primary" small style="width:80px" @click="menuOpen = false; saveVisibleColumns()">
                  {{ $t("Save") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </v-col>

        <v-col cols="1" class="d-flex align-center justify-center"><v-divider vertical class="mx-4" /></v-col>

        <v-col cols="9">
          <v-row class="mb-3">
            <v-col cols="4" class="py-0">
              <v-select v-model="filter.floor" class="form-select" hide-details :items="floors" :label="$t('Floor')"
                :placeholder="$t('Select Floor')" outlined dense clearable />
            </v-col>

            <!-- <v-col cols="3" class="py-0">
              <v-select
                v-model="filter.section"
                class="form-select"
                hide-details
                :items="sections"
                :label="$t('Section')"
                :placeholder="$t('Select Section')"
                outlined
                dense
                clearable
              />
            </v-col> -->

            <v-col cols="4" class="py-0">
              <v-select v-model="filter.error" class="form-select" hide-details :items="errors" item-title="text"
                item-value="value" :label="$t('Error')" :placeholder="$t('Select Error')" outlined dense clearable />
            </v-col>

            <v-col class="d-flex align-end py-0">
              <v-btn class="btn type-search" text @click="searchData">
                {{ $t("Search") }}
              </v-btn>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="4" class="py-0">
              <v-text-field v-model="filter.shelfId" class="form-input" hide-details :label="$t('Shelf Group ID')"
                :placeholder="$t('Input Shelf Group ID')" outlined dense clearable />
            </v-col>

            <!-- <v-col cols="3" class="py-0">
              <v-text-field
                v-model="filter.shelfName"
                class="form-input"
                hide-details
                :label="$t('Shelf Name')"
                :placeholder="$t('Input Shelf Name')"
                outlined
                dense
                clearable
              />
            </v-col> -->

            <v-col cols="4" class="py-0">
              <v-select v-model="filter.status" class="form-select" hide-details :items="statusOptions"
                item-title="text" item-value="value" :label="$t('Status')" :placeholder="$t('Select Status')" outlined
                dense clearable />
            </v-col>

            <v-col class="d-flex align-end py-0">
              <v-btn text class="btn type-search" @click="clearFilters">
                {{ $t("Clear") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-data-table :headers="filteredHeaders" :items="tableItems" item-key="shelfGroupId" fixed-header
        :item-class="getRowClass" :hide-default-footer="true" class="tbl-type01 mt-6 products">
        <template v-slot:item.expand="{ item }">
          <v-btn v-if="!item.isChild && item.children && item.children.length" icon small @click="toggleExpand(item)">
            <v-icon>
              {{
                expandedRows.includes(item.id)
                  ? "mdi-chevron-up"
                  : "mdi-chevron-down"
              }}
            </v-icon>
          </v-btn>
        </template>
        <template v-slot:item.outOfStock="{ item }">
          <span :class="{ 'txt-danger': Number(item.outOfStock) > 0 }">
            {{ item.outOfStock || 0 }}
          </span>
        </template>

        <template v-slot:item.lowStock="{ item }">
          <span :class="{ 'txt-stable': Number(item.lowStock) > 0 }">
            {{ item.lowStock || 0 }}
          </span>
        </template>

        <template v-slot:item.misplaced="{ item }">
          <span :class="{ 'txt-caution': Number(item.misplaced) > 0 }">
            {{ item.misplaced || 0 }}
          </span>
        </template>
        <template v-slot:item.details="{ item }">
          <v-icon v-if="!item.isChild" :disabled="btnDisabledDetails" small @click="openDetails(item)">mdi-file-eye</v-icon>
        </template>
        <template v-slot:item.status="{ item }">
          <v-tooltip
            v-if="item.msg && item.msg !== '-'"
            top
          >
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                :class="statusStyle(item.status)"
                style="font-weight: 600;"
              >
                {{ item.status }}
              </span>
            </template>
            <span>{{ item.msg }}</span>
          </v-tooltip>
          <span
            v-else
            :class="statusStyle(item.status)"
            style="font-weight: 600;"
          >
            {{ item.status }}
          </span>
        </template>
      </v-data-table>

      <v-row class="mt-4">
        <v-col cols="12" class="d-flex justify-space-between align-end">
          <div>
            <v-btn class="btn mr-2" text :disabled="btnDisabledDetails" @click="captureScheSetting()">{{
              $t("Schedule Settings")
            }}</v-btn>
            <v-btn class="btn mr-2" text :disabled="btnDisabledExport || (tableItems.length === 0)" @click="exportRecords()">{{
              $t("Export")
            }}</v-btn>
            <v-btn class="btn mr-2" text :disabled="btnDisabledDetails" @click="deleteRepeatSchedule()">{{
              $t("Delete Repeat Schedule")
            }}</v-btn>
          </div>
          <div>
            <Pagination @paging="paging($event)" :pageInfoText="pageInfoText" :pageIndex="pageIndex"
              :rowPerPage="rowPerPage" :goToPageInput="goToPageInput" :totalPages="totalPages"
              @updatePage="updatePage($event)"></Pagination>
          </div>
        </v-col>
      </v-row>
      <a ref="link" :style="{ display: 'none' }" />
    </div>
    <Capture-Schedule-Setting v-if="captureScheduleDetailsDailog"
      :captureScheduleDetailsDailog="captureScheduleDetailsDailog"
      @closeModalCapture="captureScheduleDetailsDailog = false" />
    <detailPage v-if="showDetailPage" :shelf-data="selectedShelf" @back="showDetailPage = false" />
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import Pagination from '@/components/pagination/Pagination.vue'
import ShelfMonitoringDetail from './ShelfMonitoringDetail.vue'
import commons from '@/plugins/commons'
import codes from '@/plugins/codes'
import debounce from 'lodash/debounce'
import { mapGetters } from 'vuex'
import EventBus from '@/plugins/eventBus'
import CaptureScheduleSetting from './model/CaptureScheduleSetting.vue'
import { getRequestConfig, exportFiles } from '@/plugins/exporter'
export default {
  components: {
    apexchart: VueApexCharts,
    Pagination,
    detailPage: ShelfMonitoringDetail,
    CaptureScheduleSetting
  },
  data () {
    return {
      requestConfig: {},
      selectedRange: 'Day',
      ranges: ['Day', 'Week', 'Month'],
      oosCount: 0,
      lowCount: 0,
      misCount: 0,
      statusSeries: [],
      statusChartOptions: {
        chart: {
          type: 'donut',
          toolbar: {
            show: false
          }
        },
        colors: ['#E76067', '#6EA5D6', '#A7ABB2'],
        labels: ['OOS', 'Low', 'Misplaced'],
        legend: {
          show: false
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          width: 2,
          colors: ['#fff']
        },
        plotOptions: {
          pie: {
            expandOnClick: false,
            donut: {
              size: '93%',
              labels: {
                show: false
              }
            }
          }
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val
            }
          }
        },
        states: {
          hover: {
            filter: {
              type: 'none'
            }
          },
          active: {
            filter: {
              type: 'none'
            }
          }
        }
      },
      trendSeries: [],
      trendOptions: {
        chart: {
          type: 'bar',
          stacked: true,
          toolbar: {
            show: false
          },
          width: '100%'
        },
        stroke: {
          colors: ['#fff'],
          curve: 'smooth'
        },
        colors: ['#E76067', '#6EA5D6', '#A7ABB2'],
        plotOptions: {
          bar: {
            columnWidth: '50%'
          }
        },
        legend: {
          show: false
        },
        xaxis: {
          categories: [],
          tickAmount: 8,
          labels: {
            rotate: 0,
            hideOverlappingLabels: true,
            trim: true,
            showDuplicates: false,
            maxHeight: 120,
            style: {
              fontSize: '12px'
            },
            formatter: val => {
              return val ? val.replace(/-/g, ' - ') : ''
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              fontSize: '12px'
            }
          }
        },
        dataLabels: {
          enabled: false
        },
        grid: {
          strokeDashArray: 0
        }
      },
      selectedColumns: [],
      floors: [],
      sections: [],
      errors: [
        { text: this.$t('Out Of Stock'), value: 'oos' },
        { text: this.$t('Low Stock'), value: 'ls' },
        { text: this.$t('Misplaced Stock'), value: 'mis' }
      ],
      statusOptions: [
        { text: this.$t('SUCCESS'), value: 'SUCCESS' },
        { text: this.$t('FAILURE'), value: 'FAILURE' }
      ],
      filter: {
        floor: null,
        // section: null,
        error: null,
        shelfId: '',
        // shelfName: '',
        status: null
      },
      menuOpen: false,
      visibleColumns: [],
      expandedRows: [],
      rawData: [],
      showDetailPage: false,
      selectedShelf: null,
      page: 1,
      totalPages: 1,
      pageIndex: 1,
      rowPerPage: '10',
      rowPerPageList: [5, 10, 20, 50, 100],
      pageInfoText: '',
      goToPageInput: 1,
      totalRecords: 0,
      captureScheduleDetailsDailog: false,
      btnDisabledDetails: null,
      btnDisabledExport: null
    }
  },

  computed: {
    hasDonutData () {
      return (this.statusSeries || []).some(v => Number(v) > 0)
    },
    headers () {
      return [
        { text: '', value: 'expand', width: 60, sortable: false },
        { text: this.$t('FLOOR'), value: 'floor', sortable: false },
        {
          text: this.$t('SHELF GROUP ID'),
          value: 'shelfGroupId',
          sortable: false
        },
        { text: this.$t('SECTION'), value: 'section', sortable: false },
        { text: this.$t('OUT OF STOCK'), value: 'outOfStock', sortable: false },
        { text: this.$t('LOW STOCK'), value: 'lowStock', sortable: false },
        { text: this.$t('MISPLACED'), value: 'misplaced', sortable: false },
        {
          text: this.$t('LINKED CAMERA'),
          value: 'linkedCamera',
          sortable: false
        },
        { text: this.$t('STATUS'), value: 'status', sortable: false },
        {
          text: this.$t('LAST CAPTURE TIME'),
          value: 'lastCaptureTime',
          sortable: false
        },
        { text: this.$t('DETAILS'), value: 'details', sortable: false }
      ]
    },
    filteredHeaders () {
      return this.headers.filter(
        h =>
          h.value === 'floor' ||
          h.value === 'expand' ||
          h.value === 'shelfGroupId' ||
          this.visibleColumns.includes(h.text)
      )
    },
    statusItems () {
      return [
        {
          label: this.$t('OOS'),
          count: this.oosCount,
          percent: this.totalCount
            ? Number(((this.oosCount / this.totalCount) * 100).toFixed(1))
            : 0,
          color: '#E76067'
        },
        {
          label: this.$t('Low'),
          count: this.lowCount,
          percent: this.totalCount
            ? Number(((this.lowCount / this.totalCount) * 100).toFixed(1))
            : 0,
          color: '#6EA5D6'
        },
        {
          label: this.$t('Misplaced'),
          count: this.misCount,
          percent: this.totalCount
            ? Number(((this.misCount / this.totalCount) * 100).toFixed(1))
            : 0,
          color: '#A7ABB2'
        }
      ]
    },
    allTableItems () {
      const result = []
      this.rawData.forEach(parent => {
        result.push({
          ...parent,
          isChild: false
        })
        if (this.expandedRows.includes(parent.id)) {
          parent.children.forEach(child => {
            result.push({
              ...child,
              floor: '',
              isChild: true,
              parentId: parent.id
            })
          })
        }
      })
      return result
    },
    tableItems () {
      return this.allTableItems
    },
    totalCount () {
      return this.misCount + this.lowCount + this.oosCount
    },
    isAllSelected () {
      return (
        this.headers.length > 0 &&
        this.visibleColumns.length === this.headers.length - 3
      )
    },
    ...mapGetters({
      store: 'dataStore/GET_SELECTED_STORE'
    })
  },
  watch: {
    store: {
      handler () {
        if (!this.store.code) {
          EventBus.$emit('openSelectedStores')
          this.oosCount = 0
          this.lowCount = 0
          this.misCount = 0
          this.statusSeries = []
          this.trendSeries = []
          this.rawData = []
          this.floors = []
          this.expandedRows = []
          this.filter = { floor: null, error: null, shelfId: '', status: null }
          this.page = 1
          this.pageIndex = 1
          this.goToPageInput = 1
          this.totalPages = 1
          this.pageInfoText = ''
          this.totalRecords = 0
          return
        }
        this.floors = []
        this.page = 1
        this.pageIndex = 1
        this.goToPageInput = 1
        this.applyRouteErrorFilter()
        this.fetchShelfGroups()
        this.fetchSummary()
        this.fetchHistory()
        this.selectedRange = 'Day'
      }
    },
    selectedRange (value) {
      let type = 'w'
      if (value === 'Day') {
        type = 'd'
      } else if (value === 'Month') {
        type = 'm'
      }
      this.fetchHistory(type)
    },
    '$route.params.error' () {
      this.applyRouteErrorFilter()
      this.searchData()
    }
  },
  methods: {
    applyRouteErrorFilter () {
      const error = this.$route.params.error
      const hasError = this.errors.some(item => item.value === error)
      this.filter.error = hasError ? error : null
    },
    fetchShelfGroups: debounce(function () {
      const params = {
        company: this.$store.state.auth.user.company,
        store: this.store.code,
        floor: this.filter.floor,
        errorType: this.filter.error,
        status: this.filter.status,
        shelfId: this.filter.shelfId || null,
        // shelfName: this.filter.shelfName,
        page: this.pageIndex - 1,
        size: Number(this.rowPerPage)
      }
      const config = { params: params }
      this.$utils
        .callAxios(
          codes.requests.togetShelfGroups.method,
          codes.requests.togetShelfGroups.url,
          config
        )
        .then(res => {
          console.log(res.config)
          this.saveRequestConfig(res.config)
          this.mapShelfGroupData(res.data)
          this.updatePagination(res.headers)
        })
        .catch(error => {
          this.rawData = []
          this.totalRecords = this.rawData.length
          const headers = {}
          headers['x-totalelements'] = 0
          this.pageInfoText = commons.getPageInfoText(headers)
          this.totalPages = 0
          this.selected = []
          console.debug(`Could not find any  record ${error}`)
        })
    }, 100),
    mapShelfGroupData (response) {
      const floorInfo = response?.shelfGroup?.floorInfo || []
      if (!this.floors.length) {
        this.floors = floorInfo.map(item => item.floor)
      }
      const operationInfo = response?.shelfGroup?.operationInfo || []
      this.rawData = operationInfo.map((item, index) => ({
        id: index + 1,
        floor: item.floor || '-',
        shelfGroupId: item.shelfGroup || '-',
        section: item.section || '-',
        outOfStock: item.oosCount,
        lowStock: item.lowCount,
        misplaced: item.misCount,
        linkedCamera: item.linkedCamera?.join(', ') || '-',
        status: item.status || '-',
        msg: item.resultMessage || '-',
        lastCaptureTime: item.lastCaptureTime ? commons.convertDate(item.lastCaptureTime) : '-',
        children: (item.cameraInfo || []).map((camera, childIndex) => ({
          id: `${index + 1}-${childIndex + 1}`,
          shelfGroupId: camera.shelfId || '-',
          floor: camera.floor || '-',
          section: camera.section || '-',
          outOfStock: camera.errorType?.oos,
          lowStock: camera.errorType?.ls,
          misplaced: camera.errorType?.mis,
          linkedCamera: camera.labelCode || '-',
          status: camera.status || '-',
          msg: camera.resultMessage || '-',
          lastCaptureTime: camera.lastCaptureTime ? commons.convertDate(camera.lastCaptureTime) : '-'
        }))
      }))
    },
    deleteRepeatSchedule () {
      this.$utils
        .callAxios(
          codes.requests.toDeleteScheduleSettings.method,
          codes.requests.toDeleteScheduleSettings.url,
          { params: { store: this.store.code } }
        )
        .then(res => {
          if (res.status === 200) {
            EventBus.$emit('messageAlert', this.$t(res.data.returnMsg))
          }
        })
        .catch(error => {
          console.log(error)
          EventBus.$emit(
            'messageAlert',
            this.$t(error.response.data.returnMsg),
            'error'
          )
        })
    },
    fetchSummary: debounce(function () {
      const params = {
        store: this.store.code
      }
      const config = { params: params }
      this.$utils
        .callAxios(
          codes.requests.togetVisionSummary.method,
          codes.requests.togetVisionSummary.url,
          config
        )
        .then(res => {
          const data = res.data
          if (!data) return
          this.oosCount = data.summaryData.oosCount || 0
          this.lowCount = data.summaryData.lowCount || 0
          this.misCount = data.summaryData.misCount || 0
          this.statusSeries = [this.oosCount, this.lowCount, this.misCount]
        })
        .catch(err => {
          console.debug(`Could not find any  record ${err}`)
        })
    }, 100),
    fetchHistory: debounce(function (type = 'd') {
      const params = {
        store: this.store.code,
        type
      }
      const config = { params: params }
      if (type === 'd') {
        config.params.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
      }
      this.$utils
        .callAxios(
          codes.requests.togetShelfHistory.method,
          codes.requests.togetShelfHistory.url,
          config
        )
        .then(res => {
          if (res.data) {
            const data = res.data.data
            if (!data.length) {
              this.trendSeries = []
              this.trendOptions = {
                ...this.trendOptions,
                xaxis: {
                  ...this.trendOptions.xaxis,
                  categories: []
                }
              }
              return
            }
            this.trendOptions = {
              ...this.trendOptions,
              xaxis: {
                ...this.trendOptions.xaxis,
                categories: data.map(item => item.date)
              }
            }
            this.trendSeries = [
              {
                name: 'OOS',
                data: data.map(item => item.oosCount)
              },
              {
                name: 'Low Stock',
                data: data.map(item => item.lowCount)
              },
              {
                name: 'Misplaced',
                data: data.map(item => item.misCount)
              }
            ]
          }
        })
        .catch(error => {
          console.error('Error fetching Shelf History:', error)
        })
    }, 100),
    captureScheSetting () {
      this.captureScheduleDetailsDailog = true
    },
    exportRecords () {
      exportFiles(
        this.requestConfig,
        this.$refs.link,
        'CameraShelfGroups.xlsx'
      )
    },
    saveRequestConfig: function (config) {
      this.requestConfig = getRequestConfig(config)
    },
    openDetails (item) {
      this.selectedShelf = item
      this.showDetailPage = true
    },
    searchData () {
      this.page = 1
      this.pageIndex = 1
      this.goToPageInput = 1
      this.expandedRows = []
      this.fetchShelfGroups()
    },
    clearFilters () {
      this.filter = {
        floor: null,
        // section: null,
        error: null,
        shelfId: '',
        // shelfName: '',
        status: null
      }
      this.searchData()
    },
    toggleSelectAll (value) {
      if (value) {
        this.visibleColumns = this.headers.map(h => h.text).slice(3)
      } else {
        this.visibleColumns = []
      }
    },
    toggleExpand (item) {
      const index = this.expandedRows.indexOf(item.id)
      if (index > -1) {
        this.expandedRows.splice(index, 1)
      } else {
        this.expandedRows.push(item.id)
      }
    },
    getRowClass (item) {
      return item.isChild ? 'child-table-row' : ''
    },
    statusStyle (status) {
      if (status === 'SUCCESS') return 'txt-stable'
      if (status === 'FAILURE') return 'txt-danger'
      return ''
    },
    paging (page = this.page) {
      this.page = page
      this.pageIndex = page
      this.goToPageInput = page
      this.fetchShelfGroups()
    },
    updatePage (event) {
      this.rowPerPage = event.toString()
      this.paging(1)
    },
    updatePagination (headers) {
      this.totalPages = Number(headers['x-totalpages'])
      this.pageIndex = Number(headers['x-number']) + 1
      this.page = this.pageIndex
      this.goToPageInput = this.pageIndex
      this.totalRecords = Number(headers['x-totalelements'])
      this.pageInfoText = commons.getPageInfoText(headers)
    },
    togetCustomCloumn () {
      const params = { userId: this.$store.state.auth.user.account, type: 'SHELF_MONITORING' }
      const config = { params: params }
      this.$utils
        .callAxios(
          codes.requests.getCustomColoumnlist.method,
          codes.requests.getCustomColoumnlist.url,
          config
        )
        .then(res => {
          if (res.status === 200) {
            console.log(res.data)
            const data = res.data
            const apiColumns = data['14300'] || []
            if (apiColumns.length > 0) {
              this.visibleColumns = apiColumns.filter(text =>
                this.headers.some(h => h.text === text)
              )
            } else {
              this.visibleColumns = []
            }
          } else {
            this.visibleColumns = this.headers.map(h => h.text)
          }
        })
        .catch(error => {
          console.log(error)
          this.visibleColumns = this.headers.map(h => h.text)
        })
    },
    saveVisibleColumns () {
      const body = {
        14300: this.visibleColumns
      }
      const params = { userId: this.$store.state.auth.user.account, type: 'SHELF_MONITORING' }
      this.$utils
        .callAxiosWithBody(
          codes.requests.updateCustomColoumnlist.method,
          codes.requests.updateCustomColoumnlist.url,
          body,
          { params: params }
        )
        .then(res => {
          if (res.status === 200) {
            console.log(res.data)
            EventBus.$emit('messageAlert', this.$t(res.data.responseMessage))
            this.togetCustomCloumn()
          }
        })
        .catch(error => {
          console.log(error)
        })
    }
  },
  mounted () {
    this.$store.dispatch('auth/getDisabledBtn', '14301').then(flag => {
      this.btnDisabledDetails = flag
    })
    this.$store.dispatch('auth/getDisabledBtn', '14302').then(flag => {
      this.btnDisabledExport = flag
    })
    this.applyRouteErrorFilter()
    if (!this.store.code) {
      EventBus.$emit('openSelectedStores')
    } else {
      this.fetchShelfGroups()
      this.fetchSummary()
      this.fetchHistory('d')
    }
    this.toggleSelectAll(true)
    this.togetCustomCloumn()
  }
}
</script>

<style scoped>
.shelf-monitoring-overview {
  display: flex;
  gap: 8px;
  margin: 0 0 18px;
}

.monitor-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 336px;
  border: 1px solid #dddfe9;
  box-shadow: none !important;
  border-radius: 4px;
  background: #fff;
  padding: 0;
  overflow: hidden;
}

.monitor-card-title {
  font-size: clamp(16px, 1.5vw, 20px);
  font-weight: 600;
  color: #5c5c75;
  padding: 16px;
}

.chart-flex-container {
  display: flex;
  align-items: center;
  gap: 28px;
  height: 70%;
}

.chart-right-section {
  width: 180px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 30px;
  flex: 1 !important;
}

.chart-container-wrapper {
  position: relative;
  width: max(104px, 11vw);
  margin: auto;
}

.chart-center-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
}

.center-title {
  font-size: clamp(10px, 1vw, 14px);
  color: #7a7a7a;
}

.center-count {
  font-size: clamp(16px, 1.5vw, 28px);
  font-weight: 700;
  color: #252b37;
}

.status-item {
  display: flex;
  align-items: start;
  gap: 5px;
}

.item-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-label {
  color: #50555c;
  font-size: clamp(8px, 0.9vw, 12px);
  font-weight: 600;
  line-height: 16px;
}

.empty-donut-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  color: #999;
  background: #F9F9F9;
  border-radius: 4px;
}

.empty-donut-icon {
  font-size: 48px;
}

.empty-donut-text {
  font-size: 13px;
  font-weight: 500;
  margin: 0 0 8px 0;
  color: #666;
}

.item-value {
  color: #161616;
  font-size: clamp(14px, 1.5vw, 28px);
  font-weight: 600;
  line-height: 16px;
}

.item-percentage {
  color: #636363;
  font-size: clamp(8px, 0.9vw, 12px);
  font-weight: 600;
  line-height: 16px;
}

.status-dot,
.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}

.monitor-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 0 0 auto;
  flex-wrap: wrap;
  padding: 16px 12px 0 16px;
}

.trend-tools {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.trend-legend {
  display: flex;
  gap: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #50555c;
  font-size: 12px;
  line-height: 16px;
}

.oos {
  background: #e76067;
}

.low {
  background: #6ea5d6;
}

.misplaced {
  background: #a7abb2;
}

.trend-range-toggle {
  display: inline-flex;
  border: 1px solid #d6d9de;
  border-radius: 4px;
  overflow: hidden;
}

.trend-range-btn {
  cursor: pointer;
  border: none;
  border-right: 1px solid #d6d9de;
  background: #f4f5f7;
  color: #6a7078;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  min-width: 56px;
  height: 28px;
}

.trend-range-btn.active {
  background: #fff;
  color: #1e1e1e;
}

.custom-label-for-input {
  font-size: 14px;
  font-weight: 600;
}

.select-column {
  max-width: 250px;
}

.columnMenu {
  max-width: 260px;
  top: 250px;
}

.column-card {
  width: 100%;
  max-width: 260px;
}

.column-header {
  font-weight: 600;
  font-size: 14px;
  padding: 12px 16px;
}

.column-list {
  max-height: 260px;
  overflow-y: auto;
  padding: 0 8px;
}

.column-text {
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.column-footer {
  position: sticky;
  bottom: 0;
  background: #fff;
  padding: 8px 12px;
}

::v-deep .v-input--selection-controls {
  margin-top: 0px !important;
  padding-top: 0px !important;
}

::v-deep .type-search.v-btn:not(.v-btn--round).v-size--default {
  height: 40px;
  width: 150px;
}

.rotate {
  transform: rotate(180deg);
  transition: transform 0.2s ease;
}

::v-deep .v-data-table thead th {
  background-color: #ececec !important;
  white-space: nowrap;
}

::v-deep ::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::v-deep ::-webkit-scrollbar-track {
  background: var(--lightestgrey);
}

::v-deep ::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 5px;
}

::v-deep ::-webkit-scrollbar-thumb:hover {
  background: #c1c1c1;
}

::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 5px;
}

::v-deep .child-table-row {
  background-color: rgb(247, 247, 247) !important;
}

::v-deep .child-table-row td {
  background-color: rgb(247, 247, 247) !important;
}

.status-success {
  color: #8b8b8b;
  font-weight: 700;
}

.status-failed {
  color: #e76067;
  font-weight: 700;
}

.empty-chart-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
  background: #f9f9f9;
  border-radius: 4px;
  height: 350px;
}

.empty-icon {
  font-size: 48px;
}

.empty-text {
  font-size: 13px;
  font-weight: 500;
  margin: 0 0 8px 0;
  color: #666;
}

@media (max-width: 1200px) {
  .shelf-monitoring-overview {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 1400px) {
  .chart-right-section {
    padding-left: 12px;
    padding-right: 12px;
  }
}
</style>
<style>
.v-tooltip-resize {
  width: 200px;
  word-break: break-word;
}

.v-tooltip__content {
  background: #001e38;
  color: #fff;
}

.v-tooltip__content:after {
  content: "";
  display: block;
  width: 0;
  height: 0;
  position: absolute;
  border-left: 15px solid transparent;
  border-right: 15px solid transparent;
  border-top: 15px solid #001e38;
  left: 50%;
  transform: translateX(-50%);
  bottom: -10px;
}
</style>
